"use client";

import { useCallback, useMemo, useState } from "react";

import { Bookmark, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { useSavedBrands } from "@/contexts/SavedBrandsContext";
import { useSignUpPrompt } from "@/contexts/SignUpPromptContext";
import { cn } from "@/lib/cn";

interface SaveBrandButtonProps {
  brandName: string;
  brandSlug?: string;
  brandUuid?: string;
  brandDescription?: string;
}

export default function SaveBrandButton({
  brandName,
  brandSlug,
  brandUuid,
  brandDescription,
}: SaveBrandButtonProps) {
  const { user, isAuthenticated } = useAuth();
  const { showSignUpPrompt } = useSignUpPrompt();
  const { createSavedBrand, deleteSavedBrand, getSavedBrandByName, loading } = useSavedBrands();
  const [isUpdatingSavedBrand, setIsUpdatingSavedBrand] = useState(false);
  const [hasError, setHasError] = useState(false);

  const sanitizedBrandName = useMemo(() => brandName.trim(), [brandName]);
  const savedBrand = getSavedBrandByName(sanitizedBrandName);
  const isSaved = Boolean(savedBrand);

  const handleSave = useCallback(async () => {
    // Prompt sign-in for logged-out or anonymous users
    if (!user || !isAuthenticated) {
      showSignUpPrompt("brand");
      return;
    }

    if (!sanitizedBrandName) {
      toast.error("Unable to save this brand. Please try again.");
      return;
    }

    if (loading || isUpdatingSavedBrand) {
      return;
    }

    setHasError(false);
    setIsUpdatingSavedBrand(true);

    try {
      if (savedBrand) {
        const success = await deleteSavedBrand(savedBrand.id);
        if (!success) throw new Error("Delete saved brand failed");
        toast.success("Brand removed");
      } else {
        const created = await createSavedBrand({
          brandName: sanitizedBrandName,
          brandSlug,
          brandUuid,
          brandDescription,
        });
        if (!created) throw new Error("Create saved brand failed");
        toast.success("Brand saved");
      }
    } catch (_error) {
      setHasError(true);
      toast.error("Failed to update favorites");
    } finally {
      setIsUpdatingSavedBrand(false);
    }
  }, [
    brandDescription,
    brandSlug,
    brandUuid,
    createSavedBrand,
    deleteSavedBrand,
    isAuthenticated,
    isUpdatingSavedBrand,
    loading,
    savedBrand,
    sanitizedBrandName,
    showSignUpPrompt,
    user,
  ]);

  const ariaLabel = isSaved
    ? `Remove ${sanitizedBrandName || "this brand"} from saved brands`
    : `Save ${sanitizedBrandName || "this brand"} to favorites`;

  return (
    <button
      onClick={handleSave}
      disabled={loading || isUpdatingSavedBrand}
      aria-label={ariaLabel}
      aria-pressed={isSaved}
      className={cn(
        "flex h-[31px] items-center gap-2.5 rounded-full border border-ink bg-white px-5 text-[11px] font-medium text-black",
        "transition-all duration-200 hover:bg-ink hover:text-white",
        "disabled:cursor-not-allowed disabled:opacity-60",
        isSaved && "border-ink bg-ink text-white"
      )}
    >
      {isSaved ? (
        <BookmarkCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      ) : (
        <Bookmark className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      )}
      {hasError ? "Retry" : isSaved ? "Saved" : "Save Brand"}
      <span className="sr-only" aria-live="polite">
        {isSaved ? "Brand is saved" : "Brand is not saved"}
      </span>
    </button>
  );
}
